import { useState } from 'react';
import {
  rescueEscortDefaults,
  rescueRadioDefaults,
  rescueScenarioDefaults,
  rescuesDefaults,
  rescueTrappedDefaults,
  rescueWavesDefaults,
} from '../../../lib/s2/defaults';
import { secondDoorSeconds, waveClock, type WaveClock } from '../../../lib/s2/rescueClock';
import {
  RESCUE_FREE_KINDS,
  RESCUE_SLOTS,
  type RescueRadioDef,
  type RescueRulesDef,
  type RescueScenarioDef,
  type RescueTrappedDef,
  type RescueTwistDef,
  type RescueFreeDef,
} from '../../../lib/s2/types';
import { withDefaults } from '../../../lib/teamsDefaults';
import { issuesUnder, type FieldErrors } from '../../../lib/validate';
import {
  CheckboxField,
  Field,
  Fieldset,
  IssueList,
  ListEditor,
  NumberField,
  NumberInput,
  SelectField,
  StringListEditor,
  TextField,
  inputCls,
  smallBtn,
} from '../fields';
import { useContentBundle } from '../refs';
import type { FormProps } from '../types';
import { TierSelectField } from '../wind/WindForms';

type Rules = Required<RescueRulesDef>;
type Scn = Required<RescueScenarioDef>;

/** A scenario with its C# defaults, nested blocks too. */
function rescueScenarioWithDefaults(s: RescueScenarioDef | null | undefined): Scn {
  const d = rescueScenarioDefaults();
  const v = withDefaults(d, (s ?? {}) as Scn);
  return {
    ...v,
    free: withDefaults(d.free, v.free ?? d.free),
    waves: withDefaults(rescueWavesDefaults(), v.waves ?? d.waves),
    beats: withDefaults(d.beats, v.beats ?? d.beats),
    trapped: (v.trapped ?? []).map((t) => withDefaults(rescueTrappedDefaults(), t ?? {})),
  };
}

function rescuesWithDefaults(v: RescueRulesDef | null | undefined): Rules {
  const d = rescuesDefaults();
  const r = withDefaults(d, (v ?? {}) as Rules);
  return {
    ...r,
    radio: withDefaults(rescueRadioDefaults(), r.radio ?? d.radio),
    escort: withDefaults(rescueEscortDefaults(), r.escort ?? d.escort),
    scenarios: (r.scenarios ?? []).map(rescueScenarioWithDefaults),
  };
}

/**
 * The `rescues` section (rescue.html §5 Admin): the rules block (caps, Radio calls (on their own or LISTEN, ADR 0064), escort numbers, name pools), and the
 * scenario list and form (categories and tags, set piece, free, waves, trapped slots, beats), with the wave clock preview for squad
 * sizes 1–4 and the kill bound it adds.
 */
export function RescuesForm({ value, onChange, errors }: FormProps<RescueRulesDef>) {
  const r = rescuesWithDefaults(value);
  const [sel, setSel] = useState(0);
  const set = <K extends keyof Rules>(k: K, x: Rules[K]) => onChange({ ...r, [k]: x });
  const radio = withDefaults(rescueRadioDefaults(), (r.radio ?? {}) as Required<RescueRadioDef>);
  const setRadio = (patch: Partial<Required<RescueRadioDef>>) =>
    set('radio', { ...r.radio, ...patch } as Rules['radio']);
  const num = (
    k: keyof Rules & string,
    label: string,
    o: { hint?: string; integer?: boolean; step?: number } = {},
  ) => (
    <NumberField
      label={label}
      hint={o.hint}
      integer={o.integer}
      min={0}
      step={o.step}
      value={r[k] as number}
      issues={errors[k]}
      onChange={(x) => set(k, (x ?? 0) as never)}
    />
  );
  const scenarios = (r.scenarios ?? []).map(rescueScenarioWithDefaults);
  const current = scenarios[sel];
  return (
    <>
      <Fieldset legend="Caps">
        <CheckboxField
          label="Enabled"
          checked={!!r.enabled}
          onChange={(x) => set('enabled', x)}
          hint="Off: no rescue calls."
        />
        {num('energySurcharge', 'Energy surcharge', { integer: true })}
        {num('successCooldownHours', 'Success cooldown (h)', {
          integer: true,
          hint: '1 success per this many hours.',
        })}
        {num('successesPer7Days', 'Successes per 7 days', { integer: true })}
        {num('claimHoldHours', 'Held claim (h)', {
          integer: true,
          hint: 'A recruit held while the roster is full.',
        })}
        {num('traitChance', 'Trait chance', { step: 0.05 })}
        {num('minFreeMult', 'Min free ×', {
          step: 0.05,
          hint: 'The free time never drops below this × its base.',
        })}
        {num('waveWarnS', 'Wave warning (s)', { integer: true })}
      </Fieldset>
      <Fieldset legend="Radio: rescue calls">
        <CheckboxField
          label="Calls appear on their own"
          checked={!!radio.autoSpawn}
          onChange={(x) => setRadio({ autoSpawn: x })}
          hint="ADR 0064: the server puts the day's calls on the map by itself (no LISTEN), and held rescues join once there's room. Off: calls only come from LISTEN (old apps)."
        />
        <NumberField
          label="Gap between calls (min)"
          hint="After a call ends (answered or expired), wait this long before the next one appears. 0..1440."
          integer
          min={0}
          value={radio.autoSpawnGapMinutes}
          issues={errors['radio.autoSpawnGapMinutes']}
          onChange={(x) => setRadio({ autoSpawnGapMinutes: x ?? 0 })}
        />
        <ListEditor
          label="Calls per day by Radio level"
          hint="Index = level (0 = no Radio)."
          items={r.radio.callsPerDayByLevel ?? []}
          onChange={(x) => set('radio', { ...r.radio, callsPerDayByLevel: x })}
          make={() => 0}
          what="level"
          issues={errors['radio.callsPerDayByLevel']}
          row={(n, i, setN) => (
            <NumberInput
              label={`Level ${i} calls`}
              integer
              min={0}
              value={n}
              onChange={(x) => setN(x ?? 0)}
            />
          )}
        />
        <NumberField
          label="Short-handed: roster at most"
          integer
          min={0}
          value={r.radio.shortHandedRosterAtMost}
          issues={errors['radio.shortHandedRosterAtMost']}
          onChange={(x) => set('radio', { ...r.radio, shortHandedRosterAtMost: x ?? 0 })}
        />
        <NumberField
          label="Short-handed calls per day"
          integer
          min={0}
          value={r.radio.shortHandedCallsPerDay}
          issues={errors['radio.shortHandedCallsPerDay']}
          onChange={(x) => set('radio', { ...r.radio, shortHandedCallsPerDay: x ?? 0 })}
        />
        <NumberField
          label="Call lasts (min)"
          integer
          min={0}
          value={r.radio.callMinutes}
          issues={errors['radio.callMinutes']}
          onChange={(x) => set('radio', { ...r.radio, callMinutes: x ?? 0 })}
        />
      </Fieldset>
      <Fieldset legend="Escort">
        {(
          [
            ['speedMult', 'Speed ×'],
            ['injuredSpeedMult', 'Injured speed ×'],
            ['leashM', 'Leash (m)'],
            ['targetWeight', 'Zombie target weight', '1 = like a squad member.'],
            [
              'unstickS',
              'Unstick after (s)',
              'Teleport behind the leader after being stuck this long.',
            ],
          ] as const
        ).map(([k, label, hint]) => (
          <NumberField
            key={k}
            label={label}
            hint={hint}
            min={0}
            step={0.05}
            value={r.escort[k]}
            issues={errors[`escort.${k}`]}
            onChange={(x) => set('escort', { ...r.escort, [k]: x ?? 0 })}
          />
        ))}
      </Fieldset>
      <NamePoolsEditor
        pools={r.namePools ?? {}}
        errors={errors}
        onChange={(x) => set('namePools', x)}
      />

      <div className="mb-4 rounded border-2 border-ink bg-white p-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold">Scenarios</h3>
          <button
            type="button"
            className={smallBtn}
            onClick={() => {
              set('scenarios', [
                ...scenarios,
                rescueScenarioWithDefaults({
                  id: `rescue_${scenarios.length + 1}`,
                  title: 'New rescue',
                }),
              ]);
              setSel(scenarios.length);
            }}
          >
            Add scenario
          </button>
        </div>
        <IssueList issues={errors.scenarios} />
        <ul className="mt-2 flex flex-wrap gap-1">
          {scenarios.map((s, i) => {
            const n = issuesUnder(errors, `scenarios.${i}`).length;
            return (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => setSel(i)}
                  className={`${smallBtn} ${i === sel ? 'bg-ink text-newsprint' : ''}`}
                >
                  {s.id || `#${i + 1}`} {n > 0 && <span className="text-blam">● {n}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      {current && (
        <RescueScenarioEditor
          key={sel}
          s={current}
          base={`scenarios.${sel}`}
          pools={Object.keys(r.namePools ?? {})}
          errors={errors}
          onChange={(x) =>
            set(
              'scenarios',
              scenarios.map((y, j) => (j === sel ? x : y)),
            )
          }
          onRemove={() => {
            if (!confirm(`Remove scenario "${current.id}"?`)) return;
            set(
              'scenarios',
              scenarios.filter((_, j) => j !== sel),
            );
            setSel(Math.max(0, sel - 1));
          }}
        />
      )}
    </>
  );
}

/** Pool id → names (one per line). */
function NamePoolsEditor({
  pools,
  errors,
  onChange,
}: {
  pools: Record<string, string[]>;
  errors: FieldErrors;
  onChange: (p: Record<string, string[]>) => void;
}) {
  const [newId, setNewId] = useState('');
  return (
    <Fieldset legend="Name pools">
      {Object.entries(pools).map(([id, names]) => (
        <Field
          key={id}
          label={id}
          hint="One name per line."
          issues={issuesUnder(errors, `namePools.${id}`)}
        >
          {(fid) => (
            <div className="flex gap-1">
              <textarea
                id={fid}
                className={`${inputCls} h-20`}
                value={(names ?? []).join('\n')}
                onChange={(e) => onChange({ ...pools, [id]: e.target.value.split('\n') })}
                onBlur={(e) =>
                  onChange({
                    ...pools,
                    [id]: e.target.value
                      .split('\n')
                      .map((x) => x.trim())
                      .filter(Boolean),
                  })
                }
              />
              <button
                type="button"
                aria-label={`Remove pool ${id}`}
                className={`${smallBtn} text-blam`}
                onClick={() => {
                  const next = { ...pools };
                  delete next[id];
                  onChange(next);
                }}
              >
                ✕
              </button>
            </div>
          )}
        </Field>
      ))}
      <div className="flex items-end gap-1 sm:col-span-2">
        <input
          aria-label="New pool id"
          className={`${inputCls} font-mono`}
          placeholder="new pool id (pharmacist)"
          value={newId}
          onChange={(e) => setNewId(e.target.value)}
        />
        <button
          type="button"
          className={smallBtn}
          disabled={!newId.trim() || newId.trim() in pools}
          onClick={() => {
            onChange({ ...pools, [newId.trim()]: [] });
            setNewId('');
          }}
        >
          Add pool
        </button>
      </div>
    </Fieldset>
  );
}

function RescueScenarioEditor({
  s,
  base,
  pools,
  errors,
  onChange,
  onRemove,
}: {
  s: Scn;
  base: string;
  pools: string[];
  errors: FieldErrors;
  onChange: (s: Scn) => void;
  onRemove: () => void;
}) {
  const b = useContentBundle();
  const opts = (
    list: ({ id?: string; name?: string; title?: string } | null)[] | null | undefined,
  ) =>
    (list ?? [])
      .filter((x) => x?.id)
      .map((x) => ({ value: x!.id!, label: `${x!.name || x!.title || x!.id} (${x!.id})` }));
  const chains = opts(b.beatChains);
  const roles = opts(b.backgrounds); // recruit roles are retired (card 11): background ids
  const traits = opts(b.survivorTraits);
  const zombies = opts(b.zombies);
  const e = (p: string) => errors[`${base}.${p}`];
  const w = s.waves;
  const setW = (k: keyof typeof w, x: number | string) =>
    onChange({ ...s, waves: { ...w, [k]: x } });
  return (
    <>
      <Fieldset legend={`Scenario ${s.id}`}>
        <TextField
          label="Id"
          mono
          value={s.id}
          issues={e('id')}
          onChange={(x) => onChange({ ...s, id: x })}
        />
        <TextField
          label="Title"
          value={s.title}
          issues={e('title')}
          onChange={(x) => onChange({ ...s, title: x })}
        />
        <StringListEditor
          label="Categories"
          value={s.categoryIds}
          errors={errors}
          base={`${base}.categoryIds`}
          onChange={(x) => onChange({ ...s, categoryIds: x })}
        />
        <StringListEditor
          label="OSM tags (any)"
          hint="amenity=pharmacy; empty = any building of the categories."
          value={s.osmTagsAny}
          errors={errors}
          base={`${base}.osmTagsAny`}
          onChange={(x) => onChange({ ...s, osmTagsAny: x })}
        />
        <SelectField
          label="Scenario layer scenario"
          hint="Empty = the rescue scenario itself."
          none="(the rescue's own)"
          value={s.scenarioId}
          options={opts(b.scenarios)}
          issues={e('scenarioId')}
          onChange={(x) => onChange({ ...s, scenarioId: x ?? '' })}
        />
        <SelectField
          label="Set piece room"
          value={s.setPieceRoomId}
          options={opts(b.setPieces)}
          issues={e('setPieceRoomId')}
          onChange={(x) => onChange({ ...s, setPieceRoomId: x ?? '' })}
        />
      </Fieldset>
      <Fieldset legend="Free">
        <SelectField
          label="Kind"
          value={s.free.kind}
          options={RESCUE_FREE_KINDS.map((k) => ({ value: k, label: k }))}
          issues={e('free.kind')}
          onChange={(x) => onChange({ ...s, free: { ...s.free, kind: x ?? '' } })}
        />
        <NumberField
          label="Seconds"
          min={0}
          value={s.free.seconds}
          issues={e('free.seconds')}
          onChange={(x) => onChange({ ...s, free: { ...s.free, seconds: x ?? 0 } })}
        />
        <TextField
          label="Tool tag"
          hint="An item tag (pry) that speeds it."
          mono
          value={s.free.toolTag}
          issues={e('free.toolTag')}
          onChange={(x) => onChange({ ...s, free: { ...s.free, toolTag: x } })}
        />
        <NumberField
          label="Tool ×"
          min={0}
          step={0.05}
          value={s.free.toolMult}
          issues={e('free.toolMult')}
          onChange={(x) => onChange({ ...s, free: { ...s.free, toolMult: x ?? 1 } })}
        />
        <NumberField
          label="Second door (s)"
          hint="A second trapped person's extra free time (the garage's +40 s); 0 = one door."
          min={0}
          value={(s.free as RescueFreeDef).secondSeconds ?? 0}
          issues={e('free.secondSeconds')}
          onChange={(x) =>
            onChange({ ...s, free: { ...s.free, secondSeconds: x ?? 0 } as RescueFreeDef })
          }
        />
      </Fieldset>
      <Fieldset legend="Waves">
        {(
          [
            ['firstAtS', 'First at (s)', false],
            ['everyS', 'Every (s)', false],
            ['baseSize', 'Base size', true],
            ['growth', 'Growth per wave', true],
            ['perExtraMember', 'Per extra member', true],
            ['escortEveryS', 'Escort wave every (s)', false],
            ['escortSize', 'Escort wave size', true],
          ] as const
        ).map(([k, label, integer]) => (
          <NumberField
            key={k}
            label={label}
            integer={integer}
            min={0}
            value={w[k] as number}
            issues={e(`waves.${k}`)}
            onChange={(x) => setW(k, x ?? 0)}
          />
        ))}
        <SelectField
          label="Last wave adds"
          none="(nothing)"
          value={w.finalExtraZombieId}
          options={zombies}
          issues={e('waves.finalExtraZombieId')}
          onChange={(x) => setW('finalExtraZombieId', x ?? '')}
        />
        <TextField
          label="Spawn tag"
          mono
          hint="The set piece's spawn tag the waves come in at."
          value={w.spawnTag}
          issues={e('waves.spawnTag')}
          onChange={(x) => setW('spawnTag', x)}
        />
      </Fieldset>
      <Fieldset legend="Trapped">
        <ListEditor<RescueTrappedDef>
          label="Slots"
          items={s.trapped}
          onChange={(x) => onChange({ ...s, trapped: x })}
          make={rescueTrappedDefaults}
          what="slot"
          issues={e('trapped')}
          row={(t, i, setT) => {
            const p = `${base}.trapped.${i}`;
            return (
              <div className="grid gap-2 sm:grid-cols-3">
                <SelectField
                  label="Slot"
                  value={t.slot}
                  options={RESCUE_SLOTS.map((x) => ({ value: x, label: x }))}
                  issues={errors[`${p}.slot`]}
                  onChange={(x) => setT({ ...t, slot: x ?? '' })}
                />
                <SelectField
                  label="Name pool"
                  value={t.namePool}
                  options={pools.map((x) => ({ value: x, label: x }))}
                  issues={errors[`${p}.namePool`]}
                  onChange={(x) => setT({ ...t, namePool: x ?? '' })}
                />
                <SelectField
                  label="Role"
                  value={t.roleId}
                  options={roles}
                  issues={errors[`${p}.roleId`]}
                  onChange={(x) => setT({ ...t, roleId: x ?? '' })}
                />
                <SelectField
                  label="Trait"
                  none="(roll one)"
                  value={t.traitId ?? ''}
                  options={traits}
                  issues={errors[`${p}.traitId`]}
                  onChange={(x) => setT({ ...t, traitId: x })}
                />
                <NumberField
                  label="Start health"
                  integer
                  min={1}
                  value={t.startHealth}
                  issues={errors[`${p}.startHealth`]}
                  onChange={(x) => setT({ ...t, startHealth: x ?? 0 })}
                />
                <TierSelectField
                  label="Start tier (WIND)"
                  hint="The rescued survivor's wind tier when they join (start health stays the escort's HP)."
                  value={t.startTier ?? 'winded'}
                  issues={errors[`${p}.startTier`]}
                  onChange={(x) => setT({ ...t, startTier: x })}
                />
                <NumberField
                  label="Injured chance"
                  min={0}
                  max={1}
                  step={0.05}
                  value={t.injuredChance}
                  issues={errors[`${p}.injuredChance`]}
                  onChange={(x) => setT({ ...t, injuredChance: x ?? 0 })}
                />
                <div className="sm:col-span-3">
                  <TextField
                    label="Epitaph"
                    value={t.epitaph}
                    issues={errors[`${p}.epitaph`]}
                    onChange={(x) => setT({ ...t, epitaph: x })}
                  />
                </div>
              </div>
            );
          }}
        />
      </Fieldset>
      <Fieldset legend="Beats">
        <SelectField
          label="Briefing chain"
          value={s.beats.briefing}
          options={chains}
          issues={e('beats.briefing')}
          onChange={(x) => onChange({ ...s, beats: { ...s.beats, briefing: x ?? '' } })}
        />
        <ListEditor<RescueTwistDef>
          label="Twists"
          items={s.beats.twists ?? []}
          onChange={(x) => onChange({ ...s, beats: { ...s.beats, twists: x } })}
          make={() => ({ beatId: '', var: '', weights: {} })}
          what="twist"
          issues={e('beats.twists')}
          row={(t, i, setT) => {
            const p = `${base}.beats.twists.${i}`;
            return (
              <div className="grid gap-2 sm:grid-cols-3">
                <SelectField
                  label="Chain"
                  value={t.beatId}
                  options={chains}
                  issues={errors[`${p}.beatId`]}
                  onChange={(x) => setT({ ...t, beatId: x ?? '' })}
                />
                <TextField
                  label="Ticket var"
                  mono
                  hint="trapped.bitten"
                  value={t.var}
                  issues={errors[`${p}.var`]}
                  onChange={(x) => setT({ ...t, var: x })}
                />
                <Field
                  label="Weights"
                  hint="value=weight, comma separated (none=80, old=15, fresh=5)"
                  issues={errors[`${p}.weights`]}
                >
                  {(id) => (
                    <WeightsInput
                      id={id}
                      value={t.weights ?? {}}
                      onChange={(x) => setT({ ...t, weights: x })}
                    />
                  )}
                </Field>
              </div>
            );
          }}
        />
        <div className="sm:col-span-2">
          <button type="button" className={`${smallBtn} text-blam`} onClick={onRemove}>
            Remove this scenario
          </button>
        </div>
      </Fieldset>
      <WaveClockPreview scenario={s} />
    </>
  );
}

/** `none=80, old=15` ↔ `{none: 80, old: 15}`; keeps the text while it's being typed. */
function WeightsInput({
  id,
  value,
  onChange,
}: {
  id: string;
  value: Record<string, number>;
  onChange: (v: Record<string, number>) => void;
}) {
  const fmt = (v: Record<string, number>) =>
    Object.entries(v)
      .map(([k, w]) => `${k}=${w}`)
      .join(', ');
  const [text, setText] = useState(fmt(value));
  return (
    <input
      id={id}
      className={`${inputCls} font-mono`}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const next: Record<string, number> = {};
        for (const part of e.target.value.split(',')) {
          const [k, w] = part.split('=').map((x) => x.trim());
          if (k && w !== undefined && Number.isFinite(Number(w))) next[k] = Math.trunc(Number(w));
        }
        onChange(next);
      }}
    />
  );
}

/**
 * The timeline for squad sizes 1–4 (rescue.html "The wave clock, side by side") with both doors, and what the schedule adds to the squad
 * kill bound for a raid of the given length (`RescueRules.MaxScheduledSpawns`: escort waves fit the length).
 */
export function WaveClockPreview({ scenario }: { scenario: RescueScenarioDef }) {
  const b = useContentBundle();
  const [durationS, setDurationS] = useState(300);
  const clocks: WaveClock[] = [1, 2, 3, 4].map((n) => waveClock(scenario, b, n, durationS));
  const free = (c: WaveClock) => c.waves.filter((w) => w.phase === 'free');
  const span = Math.max(10, ...clocks.map((c) => c.freeS));
  const W = 560;
  const x = (t: number) => 70 + (t / span) * (W - 90);
  const rowH = 34;
  return (
    <section className="mb-4 rounded border-2 border-ink bg-white p-3" aria-label="Wave clock">
      <h3 className="text-sm font-bold">Wave clock</h3>
      <p className="text-xs text-bruise">
        Scheduled waves while freeing ({clocks[0]!.freeS} s base
        {secondDoorSeconds(scenario) > 0 ? ', both doors' : ''}, no tool), by squad size; live waves
        are capped at the horde&apos;s live zombies.
      </p>
      <label className="mt-1 flex items-center gap-2 text-xs">
        Raid length (s)
        <span className="w-24">
          <NumberInput
            label="Raid length"
            integer
            min={0}
            value={durationS}
            onChange={(v) => setDurationS(v ?? 0)}
          />
        </span>
      </label>
      <svg
        viewBox={`0 0 ${W} ${rowH * 4 + 24}`}
        className="mt-2 w-full max-w-2xl"
        role="img"
        aria-label="Wave timeline"
      >
        {clocks.map((c, i) => {
          const y = 10 + i * rowH;
          return (
            <g key={c.squadSize}>
              <text x={4} y={y + 16} fontSize={11}>
                Squad {c.squadSize}
              </text>
              <rect
                x={x(0)}
                y={y + 6}
                width={x(c.freeS) - x(0)}
                height={14}
                fill="var(--color-caption, #fde68a)"
                stroke="currentColor"
                strokeWidth={0.5}
              />
              {free(c).map((wv, j) => (
                <g key={j}>
                  <line
                    x1={x(wv.atS)}
                    x2={x(wv.atS)}
                    y1={y + 2}
                    y2={y + 24}
                    stroke="#c0392b"
                    strokeWidth={2}
                  />
                  <text x={x(wv.atS) + 2} y={y + 30} fontSize={9}>
                    {wv.size}
                    {wv.extra ? '+B' : ''}
                  </text>
                </g>
              ))}
            </g>
          );
        })}
        <text x={x(0)} y={rowH * 4 + 20} fontSize={9}>
          0 s
        </text>
        <text x={x(span) - 20} y={rowH * 4 + 20} fontSize={9}>
          {span} s
        </text>
      </svg>
      <table className="mt-2 text-xs">
        <thead>
          <tr className="text-left uppercase">
            <th className="pr-3">Squad</th>
            <th className="pr-3">Free waves</th>
            <th className="pr-3">Escort waves</th>
            <th>Kill bound adds</th>
          </tr>
        </thead>
        <tbody>
          {clocks.map((c) => (
            <tr key={c.squadSize}>
              <td className="pr-3">{c.squadSize}</td>
              <td className="pr-3">
                {free(c)
                  .map((w) => `${w.size}${w.extra ? '+B' : ''}@${w.atS}s`)
                  .join(', ') || 'none'}
              </td>
              <td className="pr-3">
                {c.waves
                  .filter((w) => w.phase === 'escort')
                  .map((w) => `${w.size}@${w.atS}s`)
                  .join(', ') || 'none'}
              </td>
              <td className="font-semibold">{c.killBoundAdds}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
